import axios from 'axios';
import _ from 'lodash';
import moment from 'moment';
import { CORE_API } from '../env.config.js';

import { locationAddressFieldNames } from '@unite-us/client-utils';

const createAssistanceRequest = async (coreToken, pdfFile, requestData) => {
  const { assistance_request, assistance_request: { requestor } } = requestData;
  const formData = new FormData();

  // required
  formData.append('data[attributes][description]', assistance_request.description);
  formData.append('data[attributes][service_type_id]', assistance_request.service_type_id);
  if (assistance_request.widget_submission_id) {
    formData.append('data[attributes][widget_submission_id]', assistance_request.widget_submission_id);
  }
  formData.append('data[attributes][group_id]', coreToken.provider_id);

  formData.append('data[attributes][requestor][first_name]', requestor.first_name);
  formData.append('data[attributes][requestor][last_name]', requestor.last_name);
  formData.append('data[attributes][requestor][date_of_birth]', moment.unix(requestor.date_of_birth).utc().format('YYYY-MM-DD'));
  formData.append('consent[data]', pdfFile);

  // optional
  requestor.middle_name_initial && formData.append('data[attributes][requestor][middle_name_initial]', requestor.middle_name_initial); // Not in form
  requestor.preferred_first_name && formData.append('data[attributes][requestor][preferred_first_name]', requestor.preferred_first_name); // Not in form
  formData.append('data[attributes][requestor][gender]', _.get(requestor,'gender', 'undisclosed'));
  formData.append('data[attributes][requestor][citizenship]', _.get(requestor,'citizenship', 'undisclosed')); // Not in form
  formData.append('data[attributes][requestor][ethnicity]', _.get(requestor,'ethnicity', 'undisclosed'));

  // sexuality fields
  const sexuality = _.get(requestor,'sexuality', []);
  for (var i = 0; i < sexuality.length; i++) {
    formData.append('data[attributes][requestor][sexuality][]', sexuality[i]);
  }
  if (_.includes(sexuality, 'other') && _.get(requestor,'sexuality_other')) {
    formData.append('data[attributes][requestor][sexuality_other]', _.get(requestor,'sexuality_other'));
  }

  // Preferred Spoken and Written Languages
  const record_languages = [];
  const preferred_spoken_languages = _.get(requestor, 'preferred_spoken_languages', []);
  preferred_spoken_languages.forEach(language => {
    record_languages.push({
      language_id: language,
      record_language_type: 'spoken'
    });
  });

  const preferred_written_languages = _.get(requestor, 'preferred_written_languages', []);
  preferred_written_languages.forEach(language => {
    record_languages.push({
      language_id: language,
      record_language_type: 'written'
    });
  });

  if (record_languages.length > 0){
    for (let i = 0; i < record_languages.length; i++) {
      formData.append('data[attributes][requestor][record_languages][][language_id]', record_languages[i].language_id);
      formData.append('data[attributes][requestor][record_languages][][record_language_type]', record_languages[i].record_language_type);
    }
  }
  formData.append('data[attributes][requestor][marital_status]', _.get(requestor,'marital_status', 'undisclosed')); // Not in form
  formData.append('data[attributes][requestor][race]', _.get(requestor,'race', 'undisclosed'));
  requestor.title && formData.append('data[attributes][requestor][title]', requestor.title); // Not in form
  requestor.suffix && formData.append('data[attributes][requestor][suffix]', requestor.suffix); // Not in form
  requestor.gross_monthly_income && formData.append('data[attributes][requestor][gross_monthly_income]', requestor.gross_monthly_income);

  if (
    assistance_request.form_response &&
    assistance_request.form_response.length > 0
  ) {
    const length = assistance_request.form_response.length;
    assistance_request.form_id && formData.append('data[attributes][requestor][form_response][form_id]', assistance_request.form_id);
    for (var i = 0; i < length; i++) {
      const form_response = assistance_request.form_response[i];
      assistance_request.form_response && formData.append('data[attributes][requestor][form_response][data][][id]', form_response.id);
      assistance_request.form_response && formData.append('data[attributes][requestor][form_response][data][][name]', form_response.name);
      assistance_request.form_response && formData.append('data[attributes][requestor][form_response][data][][title]', form_response.title);
      assistance_request.form_response && formData.append('data[attributes][requestor][form_response][data][][value]', form_response.value);
      assistance_request.form_response && formData.append('data[attributes][requestor][form_response][data][][valueId]', form_response.valueId);
      assistance_request.form_response && formData.append('data[attributes][requestor][form_response][data][][displayValue]', form_response.displayValue);
    }
  }

  const addressFieldNames = [
    ...locationAddressFieldNames,
    'address_type',
    'line_2',
  ];

  for (const fn of addressFieldNames) {
    if (requestor[fn]) {
      formData.append(`data[attributes][requestor][address][${fn}]`, requestor[fn]);
    }
  }

  if (requestor.military) {
    Object.keys(requestor.military).forEach((key) => {
      const val = requestor.military[key];

      if (val) {
        if (_.includes(key, '_date') || _.includes(key, 'deployment_starts_at') || _.includes(key, 'deployment_ends_at')){
          formData.append(`data[attributes][requestor][military][${key}]`, moment.unix(val).utc().format('YYYY-MM-DD'));
        } else {
          formData.append(`data[attributes][requestor][military][${key}]`, val);
        }
      }
    });
  }

  if (requestor.email_address) {
    formData.append('data[attributes][requestor][email_addresses][][email_address]', requestor.email_address);
  }
  if (requestor.phone_number) {
    formData.append('data[attributes][requestor][phone_numbers][][phone_number]', requestor.phone_number);
    formData.append('data[attributes][requestor][phone_numbers][][phone_type]', 'unknown');
  }

  const baseURL = CORE_API;

  try {
    const response = await axios.post(
      'assistance_requests',
      formData,
      {
        baseURL,
        headers: {
          Authorization: `Bearer ${coreToken.token}`,
          'X-Application-Source': 'widgets',
        },
      },
    );

    return response;
  } catch (error) {
    return error;
  }
};

export default createAssistanceRequest;
